import { apiService } from "core/services/apiService";
import { safeSetItem } from "core/utils/localStorageUtils";
import { Lineup, WorkplaceDetails } from "types";

export const fetchGracenoteLineups = async (
    workplaceId: number
): Promise<Lineup[]> => {
    const response = await apiService.get<Lineup[]>(
        `/game-master/gracenote/lineups`,
        { params: { workplace: workplaceId } }
    );

    // add static values of "DIRECTV ESPN+ Business"
    const STATIC_VALUE_FOR_LINEUP = {
        type: "STREAMING",
        device: "X",
        lineupId: "USA-ESPNPLUS-DTV",
        name: "DIRECTV ESPN+ Business",
        location: "Nationwide",
        mso: {
            id: "17581",
            name: "DIRECTV",
        },
    };

    return [...response.data, STATIC_VALUE_FOR_LINEUP];
};


// Fetch workplace details by ID
export const fetchWorkplaceById = async (
    workplaceId: string
): Promise<WorkplaceDetails> => {
    const response = await apiService.get<WorkplaceDetails>(
        `/workplaces/${workplaceId}`
    );
    const name = response.data.name;
    localStorage.setItem("workplace_name", name)

    return response.data;
};

// TV Package interfaces
export interface TvPackage {
    "@id": string;
    "@type": string;
    id: number;
    lineupId: string;
    name: string;
    description: string;
    addons: any[] | string[];
}

export interface TvPackageResponse {
    "@context": string;
    "@id": string;
    "@type": string;
    totalItems: number;
    member: TvPackage[];
}

// TV Addon interfaces
export interface TvAddon {
    "@id": string;
    "@type": string;
    id: number;
    lineupId: string;
    name: string;
    description: string;
    tvPackages: any[] | string[];
}

export interface TvAddonResponse {
    "@context": string;
    "@id": string;
    "@type": string;
    totalItems: number;
    member: TvAddon[];
}

// localStorage keys
const TV_PACKAGES_STORAGE_KEY = 'tv_packages_cache';
const TV_ADDONS_STORAGE_KEY = 'tv_addons_cache';

// Function to clear TV packages cache
export const clearTvPackagesCache = () => {
    localStorage.removeItem(TV_PACKAGES_STORAGE_KEY);
};

// Function to clear TV addons cache
export const clearTvAddonsCache = () => {
    localStorage.removeItem(TV_ADDONS_STORAGE_KEY);
};

// Fetch TV packages with localStorage caching
export const fetchTvPackages = async (useCache: boolean = true): Promise<TvPackage[]> => {
    // Check localStorage first
    if (useCache) {
        const cachedData = localStorage.getItem(TV_PACKAGES_STORAGE_KEY);
        if (cachedData) {
            try {
                const parsedData = JSON.parse(cachedData);
                if (parsedData && parsedData.packages && parsedData.packages.length > 0) {
                    return parsedData.packages;
                }
            } catch (error) {
                console.warn("Error parsing cached TV packages:", error);
            }
        }
    }

    // Fetch from API if not cached
    try {
        const response = await apiService.get<TvPackageResponse>(
            `/tv_packages`,
            { params: { page: 1, itemsPerPage: 30 } }
        );

        const packages = response.data.member || [];
        
        // Store in localStorage with quota handling
        const cacheData = JSON.stringify({
            packages,
            timestamp: Date.now()
        });
        
        if (!safeSetItem(TV_PACKAGES_STORAGE_KEY, cacheData)) {
            console.warn('Failed to cache TV packages due to quota exceeded');
        }

        return packages;
    } catch (error) {
        console.error("Error fetching TV packages:", error);
        // Try to return cached data even if API fails
        const cachedData = localStorage.getItem(TV_PACKAGES_STORAGE_KEY);
        if (cachedData) {
            try {
                const parsedData = JSON.parse(cachedData);
                if (parsedData && parsedData.packages) {
                    return parsedData.packages;
                }
            } catch (e) {
                // Ignore parsing errors
            }
        }
        return [];
    }
};

// Fetch TV addons with localStorage caching
export const fetchTvAddons = async (useCache: boolean = true): Promise<TvAddon[]> => {
    // Check localStorage first
    if (useCache) {
        const cachedData = localStorage.getItem(TV_ADDONS_STORAGE_KEY);
        if (cachedData) {
            try {
                const parsedData = JSON.parse(cachedData);
                if (parsedData && parsedData.addons && parsedData.addons.length > 0) {
                    return parsedData.addons;
                }
            } catch (error) {
                console.warn("Error parsing cached TV addons:", error);
            }
        }
    }

    // Fetch from API if not cached
    try {
        const response = await apiService.get<TvAddonResponse>(
            `/tv_addons`,
            { params: { page: 1, itemsPerPage: 30 } }
        );

        const addons = response.data.member || [];
        
        // Store in localStorage with quota handling
        const cacheData = JSON.stringify({
            addons,
            timestamp: Date.now()
        });
        
        if (!safeSetItem(TV_ADDONS_STORAGE_KEY, cacheData)) {
            console.warn('Failed to cache TV addons due to quota exceeded');
        }

        return addons;
    } catch (error) {
        console.error("Error fetching TV addons:", error);
        // Try to return cached data even if API fails
        const cachedData = localStorage.getItem(TV_ADDONS_STORAGE_KEY);
        if (cachedData) {
            try {
                const parsedData = JSON.parse(cachedData);
                if (parsedData && parsedData.addons) {
                    return parsedData.addons;
                }
            } catch (e) {
                // Ignore parsing errors
            }
        }
        return [];
    }
};